import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: "all", label: "All Photos" },
    { id: "classroom", label: "Classroom" },
    { id: "learning", label: "Learning" },
    { id: "outdoor", label: "Outdoor" },
    { id: "social", label: "Social" },
    { id: "arts", label: "Arts & Crafts" },
  ];

  const filteredImages = filter === "all" 
    ? images 
    : images.filter(img => img.category === filter);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="noise-bg min-h-screen" data-testid="gallery-page">
      {/* Hero */}
      <section className="pt-32 pb-12 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <span className="inline-block bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Photo Gallery
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Moments of Joy & Learning
            </h1>
            <p className="text-lg text-slate-600">
              Take a peek inside Good Hands Family Daycare and see the happy moments we create every day.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === category.id
                    ? "bg-yellow-400 text-slate-900"
                    : "bg-white border border-orange-100 text-slate-700 hover:bg-yellow-50"
                }`}
                data-testid={`filter-btn-${category.id}`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="masonry-grid">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="masonry-item animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div
                  className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg shadow-orange-500/5 card-hover"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img
                    src={image.url}
                    alt={image.caption}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-white font-medium">{image.caption}</p>
                      <span className="text-white/70 text-sm capitalize">{image.category}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <p className="text-slate-600">No photos found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
          <div className="relative">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors"
              data-testid="close-lightbox-btn"
            >
              <X className="w-8 h-8" />
            </button>
            {selectedImage && (
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.caption}
                  className="w-full"
                />
                <div className="bg-white p-6">
                  <p className="font-semibold text-slate-900">{selectedImage.caption}</p>
                  <span className="text-slate-500 text-sm capitalize">{selectedImage.category}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Want to See More?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Schedule a tour and experience our warm, welcoming environment in person.
          </p>
          <Link to="/book-visit">
            <Button 
              className="btn-playful"
              data-testid="gallery-cta-btn"
            >
              Book a Tour
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
